import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
} from '@angular/core';
import { eventFindInPath, isDefined } from '@fi-shared/utils';
import { ITextItemLinkModel } from '@fi-shared/fi-text-item-helper/fi-text-item-link';
import { FiDisplayAs } from '@fi-shared/types/display-as';
import { FrontendMode, MobileAppHelperBaseService } from '@fi-shared/fi-shared-services/mobile-app-helper-base.service';
import { FiTextItemHelperService } from '@fi-shared/fi-text-item-helper/fi-text-item-helper.service';
import { ITextItemMediaModel } from '@fi-shared/fi-text-item-helper/fi-text-item-media';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { FiMediaBankService } from '@fi-shared/media-bank/media-bank.service';
import { ObjectTypes } from '@fi-shared/types';
import { debounce, takeUntil } from 'rxjs/operators';
import { interval, Subject } from 'rxjs';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';
import { SharedActionsBaseService } from '@fi-shared/fi-shared-services/shared-actions-base.service';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { getObjectViewStateData } from '@fi-shared/utils/get-object-view-state-data';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiLinkToType } from '@fi-shared/fi-text-item-helper/fi-link-to-type';
import { TextEditorInsertMediaService } from '@fi-app/shared/text-editor/text-editor-insert-media.service';

@Directive({
    selector: '[fiBindHtmlCompile]',
    exportAs: 'fiBindHtmlCompile',
    standalone: false,
})
export class BindHtmlCompileDirective implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @Input('fiBindHtmlCompile') html: string;
    @Input('fiBindHtmlCompileMediaBankFileReferences') mediaBankFileReferences: IMediaBankFileReference[];
    @Input('fiBindHtmlCompileSkipRemovingNonExsitingReferences') skipRemovingNonExsitingReferences = false;
    @Input('fiBindHtmlCompileOpenAttachment') openAttachment = false;
    @Input('fiBindHtmlCompileSkipStatisticsSave') skipStatisticsSave = false;

    @Output('fiBindHtmlCompileAfter') afterUpdateHtml = new EventEmitter();

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        private mobileAppService: MobileAppHelperBaseService,
        private rendeder: Renderer2,
        private textItemHelperService: FiTextItemHelperService,
        private fiBrowserEventsService: FiBrowserEventsService,
        private mediaBankService: FiMediaBankService,
        private mediaBankHelperService: MediaBankHelperService,
        private mediaStorageService: FiMediaStorageService,
        private sharedActionsBaseService: SharedActionsBaseService,
        private stateService: FiStateService,
        private textEditorInsertMediaService: TextEditorInsertMediaService,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        const change = changes.html;
        if (change) {
            this.elementRef.nativeElement.innerHTML = isDefined(this.html) ? this.html : '';
            this.textItemHelperService.updateTextItemLinks(this.rendeder, this.elementRef.nativeElement);
            this.updateTextItemMediaImages();
            this.updateTextItemEmbded();
            this.textItemHelperService.updateIFrameSourcesAndStyles(this.elementRef.nativeElement);

            if (!this.skipRemovingNonExsitingReferences) {
                this.textItemHelperService.removeNonExistingMediaReferences(
                    this.elementRef.nativeElement,
                    this.mediaBankFileReferences,
                );
            }

            this.afterUpdateHtml.emit();
        }
    }

    ngOnInit(): void {
        this.fiBrowserEventsService
            .getWindowEventSubject$('resize')
            .pipe(
                debounce(() => interval(200)),
                takeUntil(this.ngOnDestroy$),
            )
            .subscribe(() => this.onResize());

        this.textItemHelperService.subscribeOnVimeoUpdate(this.elementRef.nativeElement, this.ngOnDestroy$);

        this.updateVimeoPlaceholders();
    }

    private updateVimeoPlaceholders() {
        this.textEditorInsertMediaService.replaceVimeoNotAvailableLabelsInHtml(this.elementRef.nativeElement);
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    private checkOpenUserTagInPost(linkElement: HTMLAnchorElement) {
        if (linkElement.matches('.UserTagInPost')) {
            const url = linkElement.href;
            const userIdMatch = url.match(/\/(\d+)/);
            if (userIdMatch) {
                const userId = parseInt(userIdMatch[1]);
                this.sharedActionsBaseService.openUserViewPopup(userId);
            }
            return true;
        }
    }

    private checkOpenDownloadableLink($event: MouseEvent, textItemInsertMediaModel: ITextItemMediaModel) {
        if (
            textItemInsertMediaModel &&
            textItemInsertMediaModel.file &&
            this.isDownloadableLink(textItemInsertMediaModel)
        ) {
            // https://chainformation.atlassian.net/browse/FI-3977
            // also prevent opening ImagePreviewComponent
            this.mediaBankHelperService.downloadMediaBankFile(textItemInsertMediaModel.file);

            if (!textItemInsertMediaModel.linkTo && textItemInsertMediaModel.file.MediaBankFileId) {
                this.mediaBankService.saveMediaBankFileDownloadInfo(textItemInsertMediaModel.file.MediaBankFileId);
            }

            return true;
        }
    }

    private checkOpenExternalLink(linkElement: HTMLAnchorElement) {
        if (this.textItemHelperService.isExternal(linkElement)) {
            if (this.mobileAppService.frontEndMode == FrontendMode.Website) {
                let url = linkElement.href;
                // commented out: https://gmail.com/?cb=33aef9cf-4844-6e96-40ca-121975d25496 returns 404
                //url += ((url.indexOf('?') === -1 ? '?' : '&') + 'cb=' + guid());
                window.open(url);
            } else {
                this.mobileAppService.openExternalLink(linkElement.href);
            }

            return true;
        }
    }

    private checkOpenInternalLinkToItem(
        textItemInsertMediaModel: ITextItemMediaModel,
        textItemLinkModel: ITextItemLinkModel,
    ) {
        let linkToData: ITextItemLinkModel;
        if (textItemInsertMediaModel && textItemInsertMediaModel.linkTo) {
            linkToData = textItemInsertMediaModel.linkTo;
        } else if (textItemLinkModel) {
            linkToData = textItemLinkModel;
        }

        if (!linkToData || !linkToData.Value || typeof linkToData.Value === typeof '') return;

        // check if we can open link using mobile app component
        if (
            this.mobileAppService.frontEndMode === FrontendMode.MobileApp ||
            this.mobileAppService.frontEndMode === FrontendMode.WebsiteIFrame
        ) {
            if (linkToData.Value.ObjectType === ObjectTypes.CheckList) {
                this.mobileAppService.openChecklistViewer(linkToData.Value.Id, linkToData.Value.Name);
                return true;
            }
            if (linkToData.Value.ObjectType === ObjectTypes.Form) {
                this.mobileAppService.openFormViewer(linkToData.Value.Id, linkToData.Value.Name);
                return true;
            }
        }

        if (
            this.mobileAppService.frontEndMode === FrontendMode.Website ||
            this.mobileAppService.frontEndMode === FrontendMode.WebsiteIFrame
        ) {
            if (linkToData.Value.ObjectType === ObjectTypes.User) {
                this.sharedActionsBaseService.openUserViewPopup(linkToData.Value.Id);
                return true;
            }

            const stateData = getObjectViewStateData(linkToData.Value.ObjectType, linkToData.Value.Id);
            if (stateData) {
                // add fromLinkTo parameter if user goes to item by clicking on 'link to' url
                const linkToSrefParams = Object.assign(stateData.srefParams, { fromLinkTo: true });
                this.stateService.go(stateData.sref, linkToSrefParams);
                return true;
            }
        }
    }

    @HostListener('click', ['$event'])
    onClick($event: MouseEvent) {
        const linkElement: HTMLAnchorElement = <any>eventFindInPath($event, 'a');
        const targetHTMLElement = $event.target as HTMLElement;
        if (targetHTMLElement.matches('img[ccm-text-item-media-model]')) {
            const textItemMediaModel: ITextItemMediaModel = JSON.parse(
                targetHTMLElement.getAttribute('ccm-text-item-media-model'),
            );

            if (
                targetHTMLElement.parentElement.nodeName.toLowerCase() !== 'a' &&
                textItemMediaModel.hasOwnProperty('linkTo')
            ) {
                let anchorElement: HTMLAnchorElement;
                if (textItemMediaModel.linkTo.Type === FiLinkToType.WebAddress) {
                    anchorElement = document.createElement('a');
                    anchorElement.href = this.textItemHelperService.getUrlForItemLinkModel(textItemMediaModel.linkTo);
                }
                const textItemLinkModel: ITextItemLinkModel = JSON.parse(
                    targetHTMLElement.getAttribute('text-item-model'),
                );

                this.handledLinkElement(anchorElement, $event, textItemMediaModel, textItemLinkModel);
            }
        }

        if (this.openAttachment) {
            this.textItemHelperService.onTextItemClick($event, this.mediaBankFileReferences, this.skipStatisticsSave);
        }

        if (!linkElement) return;

        // parse ccm-text-item-media-model && text-item-model
        let insertMediaModelElement = linkElement.matches('a[ccm-text-item-media-model]')
            ? linkElement
            : linkElement.querySelector<HTMLElement>('[ccm-text-item-media-model]');
        const textItemInsertMediaModel: ITextItemMediaModel = insertMediaModelElement
            ? JSON.parse(insertMediaModelElement.getAttribute('ccm-text-item-media-model'))
            : null;
        const textItemLinkModel: ITextItemLinkModel = JSON.parse(linkElement.getAttribute('text-item-model'));

        this.handledLinkElement(linkElement, $event, textItemInsertMediaModel, textItemLinkModel);
    }

    private handledLinkElement(
        linkElement: HTMLAnchorElement,
        $event: MouseEvent,
        textItemInsertMediaModel: ITextItemMediaModel,
        textItemLinkModel: ITextItemLinkModel,
    ) {
        const isHandled =
            (isDefined(linkElement) && this.checkOpenUserTagInPost(linkElement)) ||
            this.checkOpenDownloadableLink($event, textItemInsertMediaModel) ||
            (isDefined(linkElement) && this.checkOpenExternalLink(linkElement)) ||
            this.checkOpenInternalLinkToItem(textItemInsertMediaModel, textItemLinkModel) ||
            this.mobileAppService.checkOpenInternalLink($event);

        if (isHandled) {
            $event.preventDefault();
            $event.stopPropagation();
        }
    }

    private updateTextItemMediaLinks(nativeElement: HTMLElement) {
        Array.prototype.forEach.call(
            nativeElement.querySelectorAll('a[ccm-text-item-media-model]'),
            (linkElement: HTMLElement) => {
                const model: ITextItemMediaModel = JSON.parse(linkElement.getAttribute('ccm-text-item-media-model'));
                if (model && this.isDownloadableLink(model) && model.file) {
                    // update urls for downloadable files
                    // https://chainformation.atlassian.net/browse/FI-3807
                    const url = this.mediaStorageService.getMediaFileUrl(model.file);
                    if (url) {
                        this.rendeder.setAttribute(linkElement, 'href', url);
                    }
                }
            },
        );
    }

    private isDownloadableLink(model: ITextItemMediaModel) {
        return (
            model.displayAs === FiDisplayAs.DownloadableFile.Id ||
            (model.displayAs === FiDisplayAs.Image.Id && !model.linkTo)
        );
    }

    //@HostListener('window:resize') // HostListener raises change detection cycle, bad for performance
    private onResize() {
        this.updateTextItemMediaImages();
    }

    private updateTextItemMediaImages() {
        this.textItemHelperService.updateTextItemMediaImages(
            this.elementRef.nativeElement,
            this.mediaBankFileReferences,
        );
    }

    private updateTextItemEmbded() {
        this.textItemHelperService.updateIFrameSourcesAndStyles(this.elementRef.nativeElement);
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            // timeout for determining container's size
            this.updateTextItemMediaImages();
        }, 1);
        this.updateTextItemMediaLinks(this.elementRef.nativeElement);
    }
}
